import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Checkroom as CheckroomIcon,
  Upload as UploadIcon,
  CameraAlt as CameraAltIcon,
  Group as GroupIcon,
  Recommend as RecommendIcon,
  Collections as CollectionsIcon,
  CalendarMonth as CalendarIcon,
  Flight as FlightIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Feed as FeedIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  MoreVert as MoreVertIcon,
  AutoStories as LookBookIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';

const Navigation: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [moreAnchorEl, setMoreAnchorEl] = React.useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();

  const menuItems = [
    { text: 'Dashboard', path: '/', icon: <DashboardIcon /> },
    { text: 'Wardrobe', path: '/wardrobe', icon: <CheckroomIcon /> },
    { text: 'Upload', path: '/upload', icon: <UploadIcon /> },
    { text: 'Fit of the Day', path: '/fit-of-the-day', icon: <CameraAltIcon /> },
    { text: 'Assistant', path: '/conversations', icon: <ChatIcon /> },
    { text: 'Outfits', path: '/outfits', icon: <GroupIcon /> },
    { text: 'Look Book', path: '/look-book', icon: <LookBookIcon /> },
    { text: 'Collections', path: '/collections', icon: <CollectionsIcon /> },
    { text: 'Calendar', path: '/calendar', icon: <CalendarIcon /> },
    { text: 'Trip Planner', path: '/trip-planner', icon: <FlightIcon /> },
    { text: 'Activity', path: '/activity', icon: <FeedIcon /> },
    { text: 'Friends', path: '/friends', icon: <PeopleIcon /> },
    { text: 'Profile', path: '/profile', icon: <PersonIcon /> },
  ];

  // Split into primary (always visible) and secondary (in More menu)
  // Mobile bottom tab bar destinations (the rest live behind "More")
  const bottomTabs = ['/', '/wardrobe', '/fit-of-the-day', '/conversations'];
  const bottomValue = bottomTabs.includes(location.pathname) ? location.pathname : false;

  const primaryItems = menuItems.slice(0, 6); // Dashboard through Outfits
  const secondaryItems = menuItems.slice(6, -1); // Collections through Friends (excluding Profile)

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };
  
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: 'rgba(255,255,255,0.94)',
          backdropFilter: 'blur(12px)',
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {/* On mobile, primary navigation lives in the bottom tab bar below. */}
          <Logo
            variant={isMobile ? "icon" : "horizontal"}
            size="small"
            animated
            onClick={() => navigate('/')}
            sx={{ mr: isMobile ? 0 : 4 }}
          />
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, flexGrow: 1 }}>
              {primaryItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    color="inherit"
                    component={Link}
                    to={item.path}
                    disableRipple
                    sx={{
                      position: 'relative',
                      px: 2,
                      py: 1.25,
                      backgroundColor: 'transparent',
                      color: active ? 'primary.main' : 'text.primary',
                      transform: 'none !important',
                      boxShadow: 'none !important',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'primary.main',
                      },
                      '&:hover::after': { width: '100%', opacity: 0.35 },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        bottom: 8,
                        height: '2px',
                        width: active ? '60%' : 0,
                        opacity: active ? 1 : 0,
                        transform: 'translateX(-50%)',
                        backgroundColor: active ? 'secondary.main' : 'text.primary',
                        transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s',
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
              {(() => {
                const moreActive = secondaryItems.some(item => location.pathname === item.path);
                return (
                  <Button
                    color="inherit"
                    onClick={handleMoreMenuOpen}
                    endIcon={<MoreVertIcon />}
                    disableRipple
                    sx={{
                      position: 'relative',
                      px: 2,
                      py: 1.25,
                      backgroundColor: 'transparent',
                      color: moreActive ? 'primary.main' : 'text.primary',
                      transform: 'none !important',
                      boxShadow: 'none !important',
                      '&:hover': { backgroundColor: 'transparent', color: 'primary.main' },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '50%',
                        bottom: 8,
                        height: '2px',
                        width: moreActive ? '60%' : 0,
                        opacity: moreActive ? 1 : 0,
                        transform: 'translateX(-50%)',
                        backgroundColor: 'secondary.main',
                        transition: 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s',
                      },
                    }}
                  >
                    More
                  </Button>
                );
              })()}
            </Box>
          )}
          
          {/* User Menu */}
          <Box sx={{ ml: 'auto' }}>
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={Boolean(anchorEl) ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
              onClick={handleProfileMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem disabled>
                <Typography variant="body2">{user?.email}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={() => navigate('/profile#settings')}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>

          {/* More Menu */}
          <Menu
            anchorEl={moreAnchorEl}
            id="more-menu"
            open={Boolean(moreAnchorEl)}
            onClose={handleMoreMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {secondaryItems.map((item) => (
              <MenuItem
                key={item.path}
                onClick={() => {
                  handleMoreMenuClose();
                  navigate(item.path);
                }}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.path}
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Mobile bottom tab bar — native-app style primary navigation */}
      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (t) => t.zIndex.appBar,
            borderTop: '1px solid',
            borderColor: 'divider',
            pb: 'env(safe-area-inset-bottom)',
          }}
        >
          <BottomNavigation
            showLabels
            value={bottomValue}
            onChange={(_, value) => {
              if (value === 'more') {
                setDrawerOpen(true);
              } else {
                navigate(value);
              }
            }}
          >
            <BottomNavigationAction label="Home" value="/" icon={<DashboardIcon />} />
            <BottomNavigationAction label="Wardrobe" value="/wardrobe" icon={<CheckroomIcon />} />
            <BottomNavigationAction label="Today" value="/fit-of-the-day" icon={<CameraAltIcon />} />
            <BottomNavigationAction label="Assistant" value="/conversations" icon={<ChatIcon />} />
            <BottomNavigationAction label="More" value="more" icon={<MenuIcon />} />
          </BottomNavigation>
        </Paper>
      )}
    </>
  );
};

export default Navigation;